import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'
import SydActionMenu from '../../../../../commonDesign/SydActionMenu'
import OpalPresentationTable from '../../../../../organisms/DenaliAnalysis/tables/presentation'
import AddMetadataTypeDialog from './AddMetadataTypeDialog'
import EditMetadataTypeDialog from './EditMetadataTypeDialog'
import DeleteMetadataTypeDialog from './DeleteMetadataTypeDialog'

const useStyles = makeStyles(() => ({
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  title: {
    fontSize: '18px',
    fontWeight: 600
  },
  code: {
    fontFamily: 'monospace',
    backgroundColor: 'var(--color-neutral-200)',
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '13px'
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    backgroundColor: 'var(--color-neutral-200)'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: 'var(--color-neutral-600)'
  }
}))

const VALUE_TYPE_LABELS = {
  date: 'Date',
  string: 'Text',
  number: 'Number',
  boolean: 'Yes/No'
}

function MetadataTypeList ({ metadataTypes, templateId }) {
  const classes = useStyles()
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [editDialog, setEditDialog] = useState({ open: false, type: null })
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null })

  const handleEdit = useCallback((type) => {
    setEditDialog({ open: true, type })
  }, [])

  const handleDelete = useCallback((type) => {
    setDeleteDialog({ open: true, type })
  }, [])

  const getActions = useMemo(() => (metadataType) => [
    {
      key: 'edit',
      label: 'Edit',
      icon: 'edit',
      onClick: () => handleEdit(metadataType),
      disabled: false
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: 'delete',
      onClick: () => handleDelete(metadataType),
      disabled: false
    }
  ], [handleEdit, handleDelete])

  const columns = useMemo(() => [
    {
      accessorKey: 'displayName',
      header: 'Display Name',
      width: '200px'
    },
    {
      accessorKey: 'metadataKey',
      header: 'Key',
      width: '150px'
    },
    {
      accessorKey: 'valueType',
      header: 'Type',
      width: '120px',
      cell: ({ getValue }) => VALUE_TYPE_LABELS[getValue()]
    },
    {
      accessorKey: 'ordinal',
      header: 'Order',
      width: '80px',
      align: 'center'
    },
    {
      accessorKey: 'showInListPage',
      header: 'Show in List',
      width: '120px',
      align: 'center',
      cell: ({ getValue }) => getValue() ? 'Yes' : 'No'
    },
    {
      accessorKey: 'metadataTypeId',
      header: 'Actions',
      width: '100px',
      align: 'center',
      cell: ({ row }) => (
        <SydActionMenu
          subject={row.original}
          actions={getActions(row.original)}
        />
      )
    }
  ], [getActions])

  return (
    <div>
      <div className={classes.header}>
        <div className={classes.title}>Metadata Fields</div>
        <SydButton
          onClick={() => setAddDialogOpen(true)}
          variant='primary'
        >
          Add Metadata Field
        </SydButton>
      </div>

      {metadataTypes.length === 0 ? (
        <div className={classes.emptyState}>
          <p>No metadata fields configured for this template.</p>
          <p>Click "Add Metadata Field" to create your first field.</p>
        </div>
      ) : (
        <OpalPresentationTable
          includeHeader
          includeFooter
          data={metadataTypes}
          columns={columns}
        />
      )}

      <AddMetadataTypeDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        templateId={templateId}
      />

      {editDialog.open && (
        <EditMetadataTypeDialog
          open={editDialog.open}
          metadataType={editDialog.type}
          onClose={() => setEditDialog({ open: false, type: null })}
        />
      )}

      {deleteDialog.open && (
        <DeleteMetadataTypeDialog
          open={deleteDialog.open}
          metadataType={deleteDialog.type}
          onClose={() => setDeleteDialog({ open: false, type: null })}
        />
      )}
    </div>
  )
}

MetadataTypeList.propTypes = {
  metadataTypes: PropTypes.array.isRequired,
  templateId: PropTypes.number.isRequired
}

export default MetadataTypeList
